import manifest from "../../../../config/questionnaire-report-card-capabilities.json";
import type { QuestionType } from "./index";

export const REPORT_CARD_SHAPES = [
  "kpi_rows",
  "lift",
  "per_item_rank",
  "podium",
  "quadrant",
  "dim_rank",
  "distribution",
  "price_curve",
  "comparison",
  "quotes",
  "fallback",
  "demand_curve",
  "question",
  "scorecard",
] as const;

export type ReportCardShape = (typeof REPORT_CARD_SHAPES)[number];
export type ReportCardSlot = "kpi" | "plot" | "quotes";
export type ReportCardShapeStatus =
  | "current_generated"
  | "runtime_only"
  | "reserved";
export type ReportCardFieldPolicy = "required" | "optional" | "forbidden";
/** Every question type can feed a report card; the card's facts say which do. */
export type ReportCardSourceQuestionType = QuestionType;
export type ReportCardPodiumBasis =
  | "chosen"
  | "distribution"
  | "scored"
  | "pictures";
export type ReportCardRequirement =
  | { kind: "sources" }
  | {
      kind: "declared_binding";
      binding: "quadrantAxes" | "liftBaseline" | "comparison" | "priceCurve";
    };

export interface ReportCardShapeCapability {
  status: ReportCardShapeStatus;
  slots: ReportCardSlot[];
  sourceQuestionTypes: ReportCardSourceQuestionType[];
  sourceQuestionTypesByBasis?: Record<
    ReportCardPodiumBasis,
    ReportCardSourceQuestionType[]
  >;
  /** Presentation-only media prerequisites; never a questionnaire constraint. */
  mediaRequiredByBasis?: ReportCardPodiumBasis[];
  assetChoicesRequiredByBasis?: ReportCardPodiumBasis[];
  /** Matrix types legal only when a KPI row names its one focal item. */
  itemSourceQuestionTypes: ReportCardSourceQuestionType[];
  sourceQuestionCardinality: { min: number; max: number | null };
  minimumItemCardinality: number;
  basis: ReportCardFieldPolicy;
  item: ReportCardFieldPolicy;
  requirement: ReportCardRequirement;
  producer: string | null;
  payload: string | null;
  renderer: string | null;
}

type ReportCardCapabilityManifest = {
  schemaVersion: "questionnaire-report-card-capabilities.v1";
  shapes: Record<ReportCardShape, ReportCardShapeCapability>;
};

const parsed = manifest as unknown as ReportCardCapabilityManifest;
const declaredShapeKeys = Object.keys(parsed.shapes).sort();
const expectedShapeKeys = [...REPORT_CARD_SHAPES].sort();
if (
  parsed.schemaVersion !== "questionnaire-report-card-capabilities.v1" ||
  JSON.stringify(declaredShapeKeys) !== JSON.stringify(expectedShapeKeys)
) {
  throw new TypeError(
    "Questionnaire report-card capability manifest does not match CardShape.",
  );
}

for (const shape of REPORT_CARD_SHAPES) {
  const capability = parsed.shapes[shape];
  const completeOutputChain = Boolean(
    capability.producer && capability.payload && capability.renderer,
  );
  if (
    (capability.status === "reserved" && completeOutputChain) ||
    (capability.status !== "reserved" && !completeOutputChain)
  ) {
    throw new TypeError(
      `Questionnaire report-card shape ${shape} has an incomplete output chain.`,
    );
  }
}

/**
 * One mechanical contract from generated option to rendered card. Historical
 * parsing remains broader: lifecycle status decides whether a shape may occur
 * in a newly accepted plan, not whether immutable old bytes can be read.
 */
export const REPORT_CARD_SHAPE_CAPABILITIES = Object.freeze(parsed.shapes);

export const CURRENT_GENERATED_REPORT_CARD_SHAPES = REPORT_CARD_SHAPES.filter(
  (shape) =>
    REPORT_CARD_SHAPE_CAPABILITIES[shape].status === "current_generated",
);

export const RENDERABLE_REPORT_CARD_SHAPES = REPORT_CARD_SHAPES.filter(
  (shape) => REPORT_CARD_SHAPE_CAPABILITIES[shape].renderer !== null,
);
